// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import api from '../../api';
import Listable from './Listable';
import Checkbox from '../Checkbox';
import Warning from '../Warning';
import writtenNumber from 'written-number';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import ActionMenu from '../ActionMenu';
import Filter from './Filter';
import Spreadsheet from './spreadsheet/table.js'
import { toast } from 'react-toastify';
import { Calendar } from 'react-date-range';
import DismissContainer from '../DismissContainer';
import Highlight from '../Highlight';

import '../../styles/input.css';
import '../../styles/investments-transactions.css';
import '../../styles/accountSummary.css';
import ContextMenu from '../ContextMenu';
import helpers from './helpers'; 
import { classifySubType } from './helpers/classificationRules.js';
export default class AccountSummary extends React.Component {
    constructor(props) {
        super(props);
       
        this.inputRef = React.createRef();
        this.contentRef = React.createRef(); 
    }

    state = {
        accountId: "",
        planId: "",
        account: {},
        plan: {},
        statements: [],
        transactions: [],
        warnings: [],
        allTransactionsSelected: false,
        filteredTransactions: [],
        accountUpdated: false, 
        cells: [],
        columnDefs: [],
        rowDefs: [],
        headers: [],
        editingTransaction: null,
        showDatePicker: false,
        isSaving: false,
        editingAccount: null,
        typedInValue: '',
        scroll: {}, 
        expectedCoverage: [], 
        subTypes: {}, 
        disablingRefresh: false,
    }

    componentDidMount = () => {
        const content = document.querySelector('.main-content > div:nth-child(1) > div:nth-child(1)');
        // Set the ref. 
        this.contentRef.current = content; 
        this.contentRef.current.style.scrollBehavior = 'smooth';    

        this.scrollState = null; // To track current scroll state label

        const scrollListener = () => {
            const content = this.contentRef.current;
            if (!content) return;
    
            const listable = document.querySelector('.listable-container');
            if (!listable) return;
    
            const scrollHeight = content.scrollHeight;
            const clientHeight = content.clientHeight;
    
            // Not enough scroll content
            if (scrollHeight <= clientHeight * 3) {
                if (this.scrollState !== null) {
                    this.scrollState = null;
                    this.setState({ scroll: {} });
                }
                return;
            }
    
            const atTop = listable.getBoundingClientRect().top <= 0;
    
            const newLabel = atTop ? "Scroll to top" : "Scroll to bottom";
            if (this.scrollState !== newLabel) {
                this.scrollState = newLabel;
                this.setState({
                    scroll: {
                        label: newLabel,
                        action: atTop
                            ? this.scrollToTop
                            : this.scrollToBottom
                    }
                });
            }
        }; // Runs at most once every 200ms
    
        this.scrollListener = scrollListener;
        this.contentRef.current.addEventListener('scroll', this.scrollListener);

        const handleBulkActions = (e) => {
            const selected = this.state.transactions.filter(t => t.selected); 
            if(!selected.length) return; 
            if(!(e.ctrlKey || e.metaKey) && e.key !== 'Escape' && !e.altKey) return; 

            e.preventDefault();
            e.stopPropagation();

            if(e.key === 'Escape'){
                this.bulkDeselect();
                return; 
            }

            if(e.ctrlKey && e.key === 'p'){
                this.bulkEditType(); 
            }

            if(e.ctrlKey && e.key === 'd'){
                this.bulkDelete(); 
            }

            if(e.altKey && e.key === 'd'){
                this.editTransactionType('Deposit')
                this.bulkDeselect();
            }
            if(e.altKey && e.key === 'w'){
                this.editTransactionType('Withdrawal')
                this.bulkDeselect();
            }
            if(e.altKey && e.key === 'i'){
                this.editTransactionType('Income')
                this.bulkDeselect();
            }
            if(e.altKey && e.key === 'f'){
                this.editTransactionType('Fees')
                this.bulkDeselect();
            }
            if(e.altKey && e.key === 'o'){
                this.editTransactionType('Other transaction')
                this.bulkDeselect();
            }
            if(e.altKey && e.key === 't'){
                this.editTransactionType('Transfer')
                this.bulkDeselect();
            }
            if(e.altKey && e.key === 'u'){
                this.editTransactionType('Other')
                this.bulkDeselect();
            }
        }

        document.addEventListener('keydown', handleBulkActions); 

        this.setState({filters: {...this.props.filters}});
        this.init(); 
    }

    componentDidUpdate = (prevProps, prevState) => {

        // When do we need to reload the data? 
        if(
            prevProps.match.params.accountId !== this.props.match.params.accountId ||
            prevProps.match.params.planId !== this.props.match.params.planId ||
            prevProps.planYear !== this.props.planYear
        )
            this.init(); 

        // When do we need to refilter the transactions? 
        if(
            JSON.stringify(this.props.filters) !== JSON.stringify(this.state.filters) ||
            this.props.query !== prevProps.query 
        ){
            // Get all of the transactions and deselect them.  
            this.setState({allTransactionsSelected: false});
            const transactions = this.state.transactions.map(t => ({
                ...t, 
                selected: false
            }))
            // The filter may have changed, set it. 
            this.setState({filters: {...this.props.filters}, transactions}, () => {
                this.filterTransactions();
            })
        }

        if(JSON.stringify(this.state.transactions) !== JSON.stringify(prevState.transactions)){
            this.filterTransactions(); 
        }

        // When do we need to update the table? 
        if(
            JSON.stringify(this.state.transactions) !== JSON.stringify(prevState.transactions) ||
            JSON.stringify(this.state.statements) !== JSON.stringify(prevState.statements) ||
            JSON.stringify(this.state.headers) !== JSON.stringify(prevState.headers) ||
            JSON.stringify(this.state.cells) !== JSON.stringify(prevState.cells) ||
            JSON.stringify(this.state.columnDefs) !== JSON.stringify(prevState.columnDefs) ||
            JSON.stringify(this.state.rowDefs) !== JSON.stringify(prevState.rowDefs) 
        ){
            this.formatToTable(); 
        }

        // Add a keydown event listener if we are now editing a transaction, else remove it. 
        if(!prevState.editingTransaction && this.state.editingTransaction)
            document.addEventListener('keydown', this.handleKeyDown);
        
        else if(prevState.editingTransaction && !this.state.editingTransaction)
            document.removeEventListener('keydown', this.handleKeyDown); 
    }

    handleKeyDown = (e) => {
        if(e.key === 'Escape'){
            const transactions = this.state.transactions.filter(t => t._id !== 'new'); 
            this.setState({
                editingTransaction: null,
                transactions: transactions
            });
            return; 
        }
        
        if(e.key !== 'Enter')
            return; 

        this.saveTransaction(); 
    }

    scrollToTop = () => {
        this.contentRef.current.scrollTo(0,0); 
    }

    scrollToBottom = () => {    
        this.contentRef.current.scrollTo(0, this.contentRef.current.scrollHeight); 
    } 

    init = async() => {
        this.setState({loading: true})
        await this.fetchInvestmentProviders(); 
        await this.fetchAccount(); 
        await this.fetchPlan(); 
        await this.fetchStatements();
        await this.fetchTransactions();
        await this.fetchSubTypes(); 
        await this.fetchClassificationRules(); 
        this.formatToTable(); 
        this.filterTransactions();
        this.setState({loading: false});
    }

    fetchPlan = async() => {
        const res = await api.get(`/tpa/plan/get?planId=${this.props.match.params.planId}`);
        if(!res.success)
            return; 
        this.setState({plan: {...res.data}});

        // Load all of the accounts for this plan. Allows us to switch from the breadcrumbs.  
        this.props.setPlan(res.data)

        const res2 = await api.get(`/tpa/account/list?planId=${this.props.match.params.planId}`);
        if(!res2.success)
            return; //TODO: Handle error with toast message.... 
        this.props.setAccounts(res2.accounts);
    }

    fetchAccount = async() => {
        const res = await api.get(`/tpa/account/get?accountId=${this.props.match.params.accountId}`);
        if(!res.success)
            return; //TODO: Handle error with toast message....
        this.setState({account: res.account})
        this.props.setAccount(res.account)
        this.props.setTitle(`Trust Accounting · ${res.account.provider} ${res.account.number}`)
    }

    fetchStatements = async() => {
        if(!this.state.account)
            return

        const res = await api.post("/tpa/statement/list", {
            planYear:  this.props.planYear,
            accountNumber: this.state.account.number,
            planId: this.state.plan.internalPlanId
        })
        if(!res.success)
            return;
        
        this.setState({statements: res.statements});
    }

    fetchTransactions = async() => {
        const res = await api.post("/tpa/transaction/list", {
            planYear: this.props.planYear, 
            accountNumber: this.state.account.number,
            statementIds: this.state.headers.map(h => h.data?.statement).filter(s => s), 
        });

        if(!res.success)
            return;

        let transactions = [...res.transactions]
       
        // Sort the transactions... 
        transactions = transactions.sort((a, b) => {
            const aDate = new Date(a.date); 
            const bDate = new Date(b.date);
            return aDate - bDate;
        });

        // Do we have transactions that need to be checked?  
        transactions = transactions.map(t => {
            const existing = this.state.transactions.find(t2 => t2._id === t._id);  
            return {
                ...t,
                selected: existing?.selected??false,
                disabled: existing?.disabled??false,
            }
        })

        this.setState({transactions: [...transactions]});
    }

    fetchSubTypes = async() => {
        const res = await api.get('/tpa/transaction/subtypes'); 
        if(!res.success)
            return; 
        this.setState({subTypes: res.subTypes});  
    }

    fetchInvestmentProviders = async() => {
        const res = await api.get('/tpa/investmentProvider/list');
        if (!res?.success || !res?.investmentProviders)
            return this.setState({ investmentProviders: [] });
        this.setState({
            investmentProviders: res.investmentProviders
        });
    }

    fetchClassificationRules = async () => {
        let offset = 0; 
        let limit = 100;  
        const rules = []; 
        while(true){
            const res = await api.get(`/tpa/transaction/rules/list?planId=${this.state.plan._id}&accountId=${this.state.account._id}&offset=${offset}&limit=${limit}`);
            if(!res.success)
                break; 

            rules.push(...res.rules); 
            if(res.rules.length < limit)
                break;
            offset += limit;
        }

        this.setState({classificationRules: rules}); 
    }

    checkTransactions = (transactions) => {
        const statements = [...this.state.statements].sort((a, b) => a.months.length - b.months.length);  
        transactions = transactions.sort((a, b) => 
            statements.indexOf(statements.find(s => s._id === a.statement)) 
            - statements.indexOf(statements.find(s => s._id === b.statement))
        ); 

        transactions = transactions.reduce((acc, t, i) => {
            t.errors = []; // Remove any existing errors...
            if(t.creator)
                return [...acc, t];
            const duplicate = acc.find(t2 => t2.amount === t.amount && t2.date === t.date && t2.statement !== t.statement); 
            if(duplicate && !t.errors?.some(e => e.title === "Possible duplicate transaction")){
                t.errors = [
                    ...(t.errors??[]),
                    {
                        title: "Possible duplicate transaction",
                        description: "A transaction from a different statement has the same amount and date.",
                        actions: [
                            {
                                label: <div className='a transaction-action-accept'>Accept</div>,
                                action: async() => {
                                    const transactions = this.state.transactions.map(t2 => {
                                        if(t2._id === t._id)
                                            return {...t2, creator: this.props.user._id}
                                        return t2; 
                                    });

                                    this.setState({transactions});
                                    await api.post('/tpa/transaction/update', {
                                        transactionId: t._id,
                                        transactionDiff: {
                                            creator: this.props.user._id
                                        }
                                    });
                                }
                            },
                            {
                                label: <div className='a transaction-action-delete'>Delete</div>,
                                action: async() => {
                                    const transactions = this.state.transactions.filter(t2 => t2._id !== t._id);
                                    this.setState({transactions});
                                    await api.post('/tpa/transaction/remove', {
                                        transactionId: t._id
                                    });
                                }
                            },
                            {
                                label: <div className='transaction-action-filter'>Show/filter duplicates</div>,
                                action: () => {
                                    this.props.setFilter({
                                        range: {
                                            from: t.amount - 1, 
                                            to: t.amount + 1, 
                                        },
                                        settleDate: {
                                            from: {
                                                endDd: new Date(t.date).getUTCDay(),
                                                endMm: new Date(t.date).getUTCMonth() + 1,
                                            },
                                            to: {
                                                endDd: new Date(t.date).getUTCDay(),
                                                endMm: new Date(t.date).getUTCMonth() + 1,
                                            }
                                        }, 
                                        statementId: null, 
                                    })
                                }
                            }
                        ]
                    }
                ];
            }

            // Does the transaction fall outside of the statement date range? 
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const statement = this.state.statements.find(s => s._id === t.statement); 
            const header = this.state.headers.find(h => h.data?.statement === t.statement);
            const columnKey = header.columnKey;  
            const coverage = months.splice(months.indexOf(columnKey.split(" - ")[0]), columnKey.split(" - ").length > 1? months.indexOf(columnKey.split(" - ")[1])+1: 1); 
            const tDate = new Date(t.date).toLocaleDateString('en-US', {month: 'short', timeZone: 'UTC'});

            if(!coverage.includes(tDate)){
                t.errors = [
                    ...(t.errors??[]),
                    {
                        title: "Transaction outside of statement coverage",
                        description: <>This transaction takes place in <span style={{color: 'var(--color-primary)'}}>{tDate}</span>, which is outside of the statement coverage, <span style={{color: 'var(--color-primary)'}}>{columnKey}</span>.</>,
                        actions: [
                            {
                                label: <div className='a transaction-action-accept'>Accept</div>,
                                action: async() => {
                                    const transactions = this.state.transactions.map(t2 => {
                                        if(t2._id === t._id)
                                            return {...t2, creator: this.props.user._id}
                                        return t2; 
                                    });

                                    this.setState({transactions});
                                    await api.post('/tpa/transaction/update', {
                                        transactionId: t._id,
                                        transactionDiff: {
                                            creator: this.props.user._id
                                        }
                                    });
                                }
                            },
                            {
                                label: <div className='a transaction-action-delete'>Delete</div>,
                                action: async() => {
                                    const transactions = this.state.transactions.filter(t2 => t2._id !== t._id);
                                    this.setState({transactions});
                                    await api.post('/tpa/transaction/remove', {
                                        transactionId: t._id
                                    });
                                }
                            }
                        ]
                    }
                ]
            }   

            t.disabled = statement?.classifying; 

            return [...acc, t]
        }, []);


        return transactions;
    }

    filterTransactions = () => {
        let filteredTransactions = this.checkTransactions([...this.state.transactions])
        .sort((a, b) => {

            if(!a.errors.length && !b.errors.length){
                const aDate = new Date(a.date); 
                const bDate = new Date(b.date);
                return aDate - bDate;
            }
            return b.errors.length - a.errors.length; 
        })

        // Do we have a search query? 
        if(this.props.query) {
            const query = this.props.query.toLowerCase();
            filteredTransactions = filteredTransactions.filter((transaction) => {
                if(transaction._id === 'new')
                    return true; 

                let strValues = ""
                
                Object.entries(transaction).some(([key, value]) => {
                    if (key === 'date') {
                        strValues += new Date(value).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            timeZone: 'UTC'
                        }).toLowerCase();
                    }
                    else if (['txType', 'txSubType', 'description', 'amount'].includes(key)) {
                        strValues += String(value).toLowerCase();
                    }
                    return false; 
                });

                return strValues.includes(query);
            });
        }
        
        // Do we have any filters?
        const filters = this.state.filters;

        // if the settleDate filter is different from the default, filter the transactions
        if (filters.settleDate.from.endDd !== 1 || filters.settleDate.from.endMm !== 1 || filters.settleDate.to.endDd !== 31 || filters.settleDate.to.endMm !== 12) {
            filteredTransactions = filteredTransactions.reduce((acc, t) => {
                if(Object.entries({...this.state.filters}).every(([key, value]) => {

                    if(key !== 'settleDate')
                        return true; 

                    const {from, to} = value;   
                    const tDate = new Date(t.date); 

                    return (
                        tDate.getUTCMonth() >= from.endMm - 1 && 
                        tDate.getUTCMonth() <= to.endMm - 1 &&
                        ((tDate.getUTCMonth() === from.endMm - 1 && tDate.getUTCDate() >= from.endDd) || 
                         (tDate.getUTCMonth() === to.endMm - 1 && tDate.getUTCDate() <= to.endDd) || 
                         (tDate.getUTCMonth() > from.endMm - 1 && tDate.getUTCMonth() < to.endMm - 1))
                    );

                    // return date >= fromDate && date <= toDate; 
                }))
                    return [...acc, t];
                return acc 
            }, []);
        }

        // if the range filter is different from the default, filter the transactions
        if (filters.range?.from !== null || filters.range?.to !== null) {
            filteredTransactions = filteredTransactions.reduce((acc, t, i) => {
                if(Object.entries({...this.state.filters}).every(([key, value]) => {

                    if(key !== 'range')
                        return true; 

                    const from = value.from?? -Infinity; 
                    const to = value.to?? Infinity;

                    return (t.amount >= from && t.amount <= to);
                }))
                    return [...acc, t];
                return acc 
            }, []);
        }

        if(Object.values(filters).some((f) => f !== null && f.length > 0)) {
            filteredTransactions = filteredTransactions.filter((t) => {

                return Object.entries(filters).every(([key, value]) => {
                    if(value === null || value.length === 0) {
                        return true; 
                    }
                    if(key === 'description') {
                        return value.some(v => t.description.toLowerCase().includes(v.toLowerCase()));
                    }
                    else if(key === 'txType') {
                        return value.includes(t.txType);
                    }
                    else if(key === 'txSubType') {
                        return value.includes(t.txSubType);
                    }
                    return true; 
                });
            });
        }

        if(filters.statementId) {
            filteredTransactions = filteredTransactions.filter(t => t.statement === filters.statementId);
        }

        const isCreating = this.state.editingTransaction?._id === 'new'; 
        this.setState({filteredTransactions: isCreating? [this.state.editingTransaction, ...filteredTransactions]: filteredTransactions});
    }

    stringToFloatPrep(v) {
        // Convert the input to a string safely, defaulting to "0" if it's not a valid string/number
        let cleanValue = (v || "0").toString();

        // Step 1: Remove all letters and dollar signs
        cleanValue = cleanValue.replace(/[^0-9().-]/g, "");

        // Step 2: Check if the number is in parentheses, and if it should be negative
        const isNegative = cleanValue.trim().startsWith("(") && cleanValue.trim().endsWith(")");

        // Step 3: Remove all parentheses (except for the first one indicating a negative number)
        cleanValue = cleanValue.replace(/[()]/g, "");

        // Step 4: Replace any remaining parentheses that may be part of the number (e.g. "12(3" becomes "123")
        cleanValue = cleanValue.replace(/\(/g, "");

        // Step 5: Parse the cleaned string into a float
        let parsedValue = parseFloat(cleanValue) || 0;  // In case of invalid input

        // Step 6: Ensure the number has two decimal places
        parsedValue = parseFloat(parsedValue.toFixed(2));

        // Step 7: Apply negative sign if needed
        let outputValue = isNegative ? -parsedValue : parsedValue;
        
        return outputValue.toString();
    }

    formatValue(v) {
        if (!v) return '-';
    
        // Normalize input by removing unwanted characters and ensuring two decimal places
        let cleanValue = this.stringToFloatPrep(v);  // Ensure two decimal places and convert back to string
        // Determine if the value is negative (after normalizing the input)
        let isNegative = parseFloat(cleanValue) < 0;

        // remove all negative signs
        cleanValue = cleanValue.replace(/-/g, "");
        // Insert commas every three digits
        let integerPart = cleanValue.split(".")[0] || "0";
        let decimalPart = cleanValue.split(".")[1] || "00";
        let formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    
        // Construct the final formatted string
        let finalResult = formattedInteger + "." + decimalPart;
        return isNegative ? `(${finalResult})` : finalResult;
    }

    formatToTable = () => {
        const labelMapping = helpers.labelMapping;
        const metadataMapping = helpers.metadataMapping;
        const transactionTypeMapping = helpers.transactionTypeMapping; 

        // All possible months for trust accounting, assuming individual statements for each. 
        let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']; 
        // Sort the months based on plan year end... 
        let startMmIndex = (this.state.plan.endMm??12) % 12;
        months = [...months.slice(startMmIndex), ...months.slice(0, startMmIndex)];

        let statements = helpers.reduceStatements(this.state.statements, this.state.plan, this.state.transactions, this.props.team?.ta?.txDetail);

        const expectedCoverage = helpers.expectedCoverage(statements, this.state.plan, this.state.planYear); 

        // Now we need to build the headers, (as keys) and include any months that are not covered by statements...
        let headers = [
            ...statements.map(s => s.columnKey), 
            ...months.filter(m => !statements.some(s => s.months.includes(m)))
        ].sort((a, b) => months.indexOf(a.split(" - ")[0]) - months.indexOf(b.split(" - ")[0]));

        // Sort the statements... 
        statements = statements.sort((a, b) => headers.indexOf(a.columnKey) - headers.indexOf(b.columnKey));

        const columnDefs = [
            {
                key: 'rowLabel',
                style: {
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'var(--color-background)',
                }
            },
            ...headers.map(h => ({
                key: h, 
                style: {
                    alignItems: 'end',
                },
            })),
            {
                key: 'recievables',
                style: {
                    alignItems: 'end',
                }
            },
            {
                key: 'total', 
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: 'var(--color-background)',
                    alignItems: 'end'
                }
            }
        ]

        const rowDefs = [
            {
                key: 'beginningBalance', 
                style:{
                    borderTop: '1px solid var(--color-border-light)', 
                    borderBottom: '1px solid var(--color-border-light)',
                    height: 50,
                }
            },
            {key: 'deposits'},
            {key: 'withdrawals'},
            {key: 'income'},
            {key: 'fees'},
            {   
                key: 'transfers',
                style:{
                    borderBottom: '1px solid var(--color-border-light)',
                }
            },
            {key: 'loanPayments'},
            {key: 'rollovers'},
            {key: 'reinvestments'},
            {key: 'other'},
            {
                key: 'gain_loss',
                style:{
                    borderTop: '1px solid var(--color-border-light)', 
                    borderBottom: '1px solid var(--color-border-light)'
                },
            },
            {key: 'endingBalance'},
            {key: 'accrued'},
            {
                key: 'balanceAccrued',
                style:{
                    borderTop: '1px solid var(--color-border)', 
                    borderBottom: '6px double var(--color-border)'
                },
            },
        ]

        headers = columnDefs.map(def => {
            let value = def.key?.toUpperCase(); 
            if(def.key === 'rowLabel') value = '';
            if(def.key === 'total') value = 'Total';
            if(def.key === 'recievables') value = 'Recievables';

            // Are we expecting this? 
            const expecting = expectedCoverage.includes(def.key.split(" - ")[0]); 
            let data;

            if(def.key === 'rowLabel' || def.key === 'total' || def.key === 'recievables')
                data = null; 
            else
                data = {
                    missing: expecting,
                    na: !expecting,  
                    planYear: this.props.planYear, 
                    account: {...this.state.account},
                    statement: statements.find(s => s.months.includes(def.key.split(" - ")[0]))?._id??null
                }

            return {
                columnKey: def.key, 
                value, 
                data,
            }
        });

        const subTypes = {};

        // Create the individual cells for the table, and link them to the proper row/column... 
        const cells = statements.reduce((acc, statement, statementIdx) => {
            // What is the column key for this statement (Month it belongs to) 
            const columnKey = statement.columnKey; // Set above, while cleaning the statements. 

            // We need to attach data from this statement to the header. This will allow for actions (Review, approve, etc...) 
            const headerIdx = headers.findIndex(h => h.columnKey === columnKey);
            const header = headers[headerIdx];
            header.data.errorCount = 0; 
            header.data.missing = false; 
            header.data.confirmed = statement.confirmed;
            header.data.clientConfirmed = statement.clientConfirmed;
            header.data.reconciles = statement.reconciles;
            header.data.document = statement.document;
            header.data.split = statement.split; 
            header.data.splitFrom = statement.splitFrom??[];

            const isStaxUser = this.props.user.email.endsWith('@stax.ai');
            const isStatementLockedForCurrentUser = isStaxUser ? header.data?.confirmed : header.data?.clientConfirmed;

            // Get the previous and next statement... 
            const statementTransactions = this.state.transactions.filter(t => {
                return t.statement === statement._id
            });

            // For each statementTransactions, add a key indicate if the statement is confirmed.
            statementTransactions.forEach(t => {
                t.confirmed = statement.hasOwnProperty('confirmed') ? statement.confirmed: false;
                t.clientConfirmed = statement.hasOwnProperty('clientConfirmed') ? statement.clientConfirmed: false;
            })

            // For each statementTransactions, check for subtypes. 
            statementTransactions.forEach(t => {
                if(!t.txSubType) return; 

                const type = t.txType; 
                const subType = t.txSubType;
                const amount = t.amount;  
                const rowDef = Object.entries(transactionTypeMapping).find(([key, value]) => value === type)?.[0]; 

                // If the subType does not exist, create it. 
                if(!subTypes[rowDef])
                    subTypes[rowDef] = {}; 

                if(!subTypes[rowDef][subType])
                    subTypes[rowDef][subType] = {};

                if(!subTypes[rowDef][subType][columnKey])
                    subTypes[rowDef][subType][columnKey] = 0;

                subTypes[rowDef][subType][columnKey] += amount;
            })

            // For each row, build the cell that belong's to it. 
            const statementCells = rowDefs.map((rowDef, index) => {
                const confidence = {...statement.confidence, ...statement.confidence.tx}?.[rowDef.key]??1; 
                const lowConfidence = confidence <= 0.8 && confidence > 0;
                // const lowConfidence = true;


                // We need to calculate/create any errors to show for the cell.  
                // 1. Get the next statement, and the previous statement. (Used to check for mismatches) 
                // What IDX is this statement header?  


                const columnIdx = headers.findIndex(h => h.columnKey === columnKey); 
                const prevStatementId = columnIdx > 0? headers[columnIdx - 1].data?.statement: null; 
                const nextStatementId = columnIdx < headers.length - 1? headers[columnIdx + 1].data?.statement: null; 
                const previousStatement = prevStatementId? statements?.find(s => s._id === prevStatementId): null; 
                const nextStatement = nextStatementId? statements?.find(s => s._id === nextStatementId): null; 

                // 2. What are the different errors that need to be shown on this cell? 
                // - Mismatch beginning/ending balance 
                // - Low confidence value 
                // - Reconcile error     
                
                // Each error should be formatted as the following. 
                // {title: "", description: '', actions: [{label: '', action: () => {}}]
                let errors = [];            

                if(rowDef.key === 'beginningBalance'){
                    // Check the previous statement, and check that everything matches... 
                    if(previousStatement && (parseFloat(previousStatement.endingBalance?.toFixed(2)) !== parseFloat(statement.beginningBalance?.toFixed(2)) && parseFloat(statement.beginningBalance?.toFixed(2)) !== parseFloat(previousStatement.balanceAccrued?.toFixed(2)))){
                        errors.push({
                            title: 'Balances do not match',
                            level: 'high',
                            description: (
                            <>
                                The opening balance for this period
                                doesn't match the closing balance
                                for the previous period.
                                <br/><br/>
                                The gain/(loss) validation check<span
                                    style={{
                                        color: statement.reconciles?'var(--palette-color-3)':'var(--palette-color-1)'
                                    }}
                                >{statement.reconciles?' passes ':' fails '}</span>for this value. 
                            </>
                            ),
                            actions: [
                                {
                                    label: <div className='a cell-action-accept'>Accept</div>,
                                    action: async() => {
                                        // Next we need to update the state of the next statement... 
                                        const statements = [...this.state.statements].map(s => {
                                            if(s._id !== previousStatement._id) return s; 
                                            return {...s, endingBalance: statement.beginningBalance}
                                        }); 

                                        this.setState({statements}); 
                                        // We need to get the current statement's document, and the next statement's document. 
                                        let previousDocument = await api.get('/document/get?docId=' + previousStatement.document);
                                        if(!previousDocument.success)
                                            return toast.error("Failed to update the document.");
                                        previousDocument = previousDocument.doc; 

                                        // Adjust the metadata of the next document, so that the beginning balance matches the ending balance of the current document. 
                                        
                                        const metadata = [
                                            ...previousDocument.metadata.filter(m => m.key !== 'ending balance'), 
                                            {
                                                key: 'ending balance',
                                                value: this.formatValue(statement.beginningBalance).toString(),
                                                confidence: 1,
                                            }
                                        ];  

                                        // Update the document with the new metadata... 
                                        const res = await api.post('/document/update', {
                                            docId: previousStatement.document,
                                            docDiff: {metadata}
                                        });

                                        if(!res.success)
                                            return toast.error("Failed to update the document."); 
                                    }
                                },
                                {
                                    label: <div className='a cell-action-review'>Open</div>,
                                    action: () => {
                                        window.open('/document/' + statement.document, '_blank')
                                    }
                                }
                            ]
                        })
                    }
                }
                if(rowDef.key === 'endingBalance'){
                    // Check the previous statement, and check that everything matches... 
                    if(nextStatement && (parseFloat(nextStatement.beginningBalance?.toFixed(2)) !== parseFloat(statement.endingBalance?.toFixed(2)) && parseFloat(nextStatement.beginningBalance?.toFixed(2)) !== parseFloat(statement.balanceAccrued?.toFixed(2)))){
                        errors.push({
                            title: 'Balances do not match',
                            level: 'high',
                            description: (
                                <>
                                    The closing balance for this period
                                    doesn't match the opening balance
                                    for the next period.
                                    <br/><br/>
                                    The gain/(loss) validation check<span
                                        style={{
                                            color: statement.reconciles?'var(--palette-color-3)':'var(--palette-color-1)'
                                        }}
                                    >{statement.reconciles?' passes ':' fails '}</span>for this value. 
                                </>
                                ),
                            actions: [
                                {
                                    label: <div className='a cell-action-accept'>Accept</div>,
                                    action: async() => {
                                        // Next we need to update the state of the next statement... 
                                        const statements = [...this.state.statements].map(s => {
                                            if(s._id !== nextStatement._id) return s; 
                                            return {...s, beginningBalance: statement.endingBalance}
                                        }); 

                                        this.setState({statements}); 
                                        
                                        // We need to get the current statement's document, and the next statement's document. 
                                        let nextDocument = await api.get('/document/get?docId=' + nextStatement.document);
                                        if(!nextDocument.success)
                                            return toast.error("Failed to update the document.");
                                        nextDocument = nextDocument.doc; 

                                        // Adjust the metadata of the next document, so that the beginning balance matches the ending balance of the current document. 
                                        const metadata = [
                                            ...nextDocument.metadata.filter(m => m.key !== 'beginning balance'), 
                                            {
                                                key: 'beginning balance',
                                                value: this.formatValue(statement.endingBalance).toString(),
                                                confidence: 1,
                                            }
                                        ]

                                        // Update the document with the new metadata... 
                                        const res = await api.post('/document/update', {
                                            docId: nextStatement.document,
                                            docDiff: {metadata}
                                        });

                                        if(!res.success)
                                            return toast.error("Failed to update the document."); 

                                    }
                                },
                                {
                                    label: <div className='a cell-action-review'>Open</div>,
                                    action: () => {
                                        window.open('/document/' + statement.document, '_blank')
                                    }
                                }
                            ]
                        })
                    }
                }
                if(rowDef.key === 'gain_loss'){

                    // Does this statement reconcile?
                    if(!statement.reconciles){ 
                        // Calculate what the gain loss should be... 
                        // Calculate the gain loss. 
                        const {beginningBalance, endingBalance, tx:transactions} = statement;
                        const {
                            deposits, 
                            withdrawals,
                            income, 
                            fees, 
                            transfers, 
                            other,
                            gain_loss,
                            reinvestments,
                            loanPayments,
                            rollovers,
                            recievables, 
                        } = transactions;
                        

                        const calculatedGainLoss = (endingBalance??0) - (
                            (beginningBalance??0) + 
                            (deposits??0) + 
                            (withdrawals??0) + 
                            (income??0) + 
                            (fees??0) + 
                            (transfers??0) + 
                            (other??0) +
                            (reinvestments??0) +
                            (loanPayments??0) +
                            (rollovers??0) +
                            (recievables??0)    
                        );

                        // Does the gain loss match the calculated value?
                        if(gain_loss !== calculatedGainLoss){
                            errors.push({
                                title: 'Validation check failed',
                                level: 'high',
                                description: (
                                    <>
                                        The gain/(loss) extracted from the 
                                        statement does not match the
                                        calculated value. 
                                        <br/><br/>
                                        <span style={{fontWeight: 600}}>Extracted: </span>{this.formatValue(gain_loss)}
                                        <br/>
                                        <span style={{fontWeight: 600}}>Calculated: </span>{this.formatValue(calculatedGainLoss)}
                                        <br/>
                                        <span style={{fontWeight: 600}}>Difference: </span>{this.formatValue(gain_loss - calculatedGainLoss)}
                                    </>
                                    ),
                                actions: [
                                    {
                                        label: <div className='a cell-action-review'>Open statement</div>,
                                        action: () => { window.open('/document/' + statement.document, '_blank')}
                                    },
                                ]
                            });
                        }
                        else
                            statement.reconciles = true; 
                    }
                }

                if(lowConfidence && !statement.reconciles){
                    errors.push({
                        title: 'Low confidence',
                        level: 'low',
                        description: (
                            <>
                                {statement.reconciles?
                                "This value has a low confidence, but has passed the validation check.":
                                "This value has a low confidence, and has failed the validation check."
                                }
                            </>
                            ),
                        actions: [
                            ...(statement.reconciles?[
                                {
                                    label: <div className='a cell-action-accept'>Accept</div>,
                                    action: async() => {

                                        // Get the statements, and update the confidence of the statement to be 1.  
                                        const statements = [...this.state.statements].map(s => {
                                            if(s._id !== statement._id) return s;

                                            const confidence = {
                                                ...s.confidence,
                                                [rowDef.key]: 1,
                                                tx: {
                                                    ...s.confidence.tx,
                                                    [rowDef.key]: 1
                                                }
                                            }

                                            return {...s, confidence}
                                        });

                                        this.setState({statements});

                                        // Get the document, and update the metadata confidence field to be 1. 
                                        let document = await api.get('/document/get?docId=' + statement.document);
                                        if(!document.success)
                                            return toast.error("Failed to update the document's confidence");
                                        document = document.doc;

                                        const metadata = [...document.metadata].map(m => {
                                            if(m.key !== metadataMapping[rowDef.key]) return m; 
                                            return {
                                                ...m,
                                                confidence: 1
                                            }
                                        });

                                        // Update the document with the new metadata...
                                        await api.post('/document/update', {
                                            docId: statement.document,
                                            docDiff: {metadata}
                                        });
                                    }
                                },
                            ]:[]),
                            {
                                label: <div className='a cell-action-review'>Open</div>,
                                action: () => { window.open('/document/' + statement.document, '_blank')}
                            }
                        ]
                    })
                }

                // Next we need to find the transactions related to this timeframe. (We cannot use statement._id because statements may have been combined).
                // Then we need to check for transactions catagorized as this row. 
                // Then we need to check that the sum of those transactions matches the value in the statement. 

                const checkTxError = (
                    [
                        ...(this.props.team.ta?.txDetail === 'All'? ['deposits', 'withdrawals', 'income', 'fees', 'transfers']: []),
                        ...(this.props.team.ta?.txDetail === 'Cashflow'? ['deposits', 'withdrawals', 'transfers']: []), 
                    ].includes(rowDef.key)
                )

                // Get the transactions that are related to this statement...
                if(checkTxError && !statement.confirmed){
                    // Find all transactions that match this row type... 
                    const rowTransactions = statementTransactions.filter(t => t.txType === transactionTypeMapping[rowDef.key]);
                    // Calculate the sum of the transactions...
                    const rowTotalRaw = (rowTransactions.reduce((acc, t) => acc += t.amount, 0)).toFixed(2);
                    const statementTotalRaw = (statement[rowDef.key]).toFixed(2);
                    const rowTotalFormatted = this.formatValue(rowTotalRaw);
                    const statementTotalFormatted = this.formatValue(statementTotalRaw);

                    // Does the total match the value?  
                    if(rowTotalFormatted !== statementTotalFormatted && statement.accepted?.[rowDef.key] !== true){
                        errors.push({
                            title: 'Transaction check failed',
                            level: 'high',
                            description: (
                                <>
                                    The sum of transactions extracted from the 
                                    statement does not match the extracted 
                                    value. 
                                    <br/><br/>
                                    {
                                        (statement.split && statement.original?.[rowDef.key] !== statement[rowDef.key]) && 
                                        <>
                                        <span>
                                            The <span style={{fontWeight: 600}}>Extracted</span> amount was calculated by subtracting the values of <span style={{color: 'var(--color-primary)'}}>{statement.splitFrom.join(", ")}</span>, <span style={{fontWeight: 600}}>{this.formatValue(statement.original?.[rowDef.key] - statement[rowDef.key])}</span>, from <span style={{color: 'var(--color-primary)'}}>{statement.months.join(", ")}</span> <span style={{fontWeight: 600}}>{this.formatValue(statement.original?.[rowDef.key])}</span>.
                                        </span>
                                        <br/>
                                        <br/>
                                        </>
                                    }
                                    <span style={{fontWeight: 600}}>Extracted: </span>{this.formatValue(statementTotalFormatted)}
                                    <br/>
                                    <span style={{fontWeight: 600}}>Calculated: </span>{this.formatValue(rowTotalFormatted)}
                                    <br/>
                                    <span style={{fontWeight: 600}}>Difference: </span>{this.formatValue(statementTotalRaw - rowTotalRaw)}
                                </>
                                ),
                            actions: [
                                {
                                    label: <div className='a cell-action-accept'>Accept</div>,
                                    action: async() => {
                                        // We need to update the statement, and put the set the transaction type as accepted. 
                                        let update; 
                                        let statements = [...this.state.statements].map(s => {
                                            if(s._id !== statement._id) return s; 
                                            const accepted = {
                                                ...s.accepted,
                                                [rowDef.key]: true
                                            }
                                            update = {...s, accepted}
                                            return update;
                                        }); 

                                        this.setState({statements});

                                        // Next we need to make a request to the api to update the statement... 
                                        await api.post('/tpa/statement/update', {
                                            statementId: statement._id,
                                            diff: {accepted: update.accepted} 
                                        });
                                    }
                                },
                                {
                                    label: <div className='a cell-action-review'>Open</div>,
                                    action: () => { window.open('/document/' + statement.document, '_blank')}
                                },
                                {
                                    label: (
                                        <div 
                                            className='cell-action-filter'
                                        >
                                            Show/filter transactions
                                        </div>
                                    ),
                                    action: () => {
                                        // We have the month in short form, we need to convert it to a numerical value...
                                        this.props.setFilter({
                                            ...this.state.filters, 
                                            statementId: statement._id,
                                        });
                                        
                                        document.getElementById('listable-rows').scrollIntoView({behavior: 'smooth'}); 
                                    }
                                },
                                {
                                    label: (
                                        <div 
                                            className='cell-action-filter'
                                        >
                                            Create transaction
                                        </div>
                                    ),
                                    action: () => {
                                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                        const month = statement.months[statement.months.length - 1]; 
                                        const monthIndex = months.indexOf(month);

                                       const transaction = {
                                             _id: 'new',
                                             planYear: this.props.planYear, 
                                             team: this.props.team._id,
                                             date: new Date(this.props.planYear, monthIndex, 1).toUTCString(),
                                             statement: statement._id,
                                             txType: transactionTypeMapping[rowDef.key],
                                             amount: statementTotalRaw - rowTotalRaw,
                                             description: 'Manually created transaction',      
                                             planId: this.state.plan.internalPlanId,
                                             accountNumber: this.state.account.number,
                                       }

                                       this.setState({editingTransaction: transaction}, () => {
                                        this.saveTransaction(); 
                                       });
                                    }
                                },
                            ]
                        })
                    }

                    // Condition for accepted values - this is not really an error 
                    if(rowTotalFormatted !== statementTotalFormatted && statement.accepted?.[rowDef.key] === true){
                        errors.push({
                            title: 'Value accepted',
                            level: 'none',
                            description: (
                                <>
                                    The total of the transactions extracted from the statement did not match the extracted value. However, this value was accepted despite the discrepancy. 
                                    <br/><br/>
                                    {
                                        (statement.split && statement.original?.[rowDef.key] !== statement[rowDef.key]) && 
                                        <>
                                        <span>
                                            The <span style={{fontWeight: 600}}>Extracted</span> amount was calculated by subtracting the values of <span style={{color: 'var(--color-primary)'}}>{statement.splitFrom.join(", ")}</span>, <span style={{fontWeight: 600}}>{this.formatValue(statement.original?.[rowDef.key] - statement[rowDef.key])}</span>, from <span style={{color: 'var(--color-primary)'}}>{statement.months.join(", ")}</span> <span style={{fontWeight: 600}}>{this.formatValue(statement.original?.[rowDef.key])}</span>.
                                        </span>
                                        <br/>
                                        <br/>
                                        </>
                                    }
                                    <span style={{fontWeight: 600}}>Extracted: </span>{this.formatValue(statementTotalFormatted)}
                                    <br/>
                                    <span style={{fontWeight: 600}}>Calculated: </span>{this.formatValue(rowTotalFormatted)}
                                    <br/>
                                    <span style={{fontWeight: 600}}>Difference: </span>{this.formatValue(statementTotalRaw - rowTotalRaw)}
                                </>
                                ),
                            actions: [
                                {
                                    label: <div className='a cell-action-decline'>Decline</div>,
                                    action: async() => {
                                        // We need to update the statement, and put the set the transaction type as accepted. 
                                        let update; 
                                        let statements = [...this.state.statements].map(s => {
                                            if(s.document !== cell.document) return s; 
                                            const accepted = {
                                                ...s.accepted,
                                                [cell.rowKey]: false
                                            }
                                            update = {...s, accepted}
                                            return update;
                                        }); 
                    
                                        let cells = [...this.state.cells].map(c => {
                                            if (c.document !== cell.document && c.rowKey !== cell.rowKey) return c;
                                            return {
                                                ...c,
                                                data: {
                                                    ...c.data,
                                                    accepted: {
                                                        ...c.data.accepted,
                                                        [cell.rowKey]: false
                                                    }
                                                }
                                            };
                                        }); 
                    
                                        // updating the state for cells
                                        this.setState({ statements })
                                        this.setState({ cells });

                                        // Next we need to make a request to the api to update the statement... 
                                        await api.post('/tpa/statement/update', {
                                            statementId: statement._id,
                                            diff: {accepted: update.accepted} 
                                        }); 
                                    }
                                },
                                {
                                    label: <div className='a cell-action-review'>Open</div>,
                                    action: () => { window.open('/document/' + statement.document, '_blank')}
                                },
                                {
                                    label: (
                                        <div 
                                            className='cell-action-filter'
                                        >
                                            Show/filter transactions
                                        </div>
                                    ),
                                    action: () => {
                                        // We have the month in short form, we need to convert it to a numerical value...
                                        this.props.setFilter({
                                            ...this.state.filters, 
                                            statementId: statement._id,
                                        });
                                        
                                        document.getElementById('listable-rows').scrollIntoView({behavior: 'smooth'}); 
                                    }
                                },
                            ]
                        })
                    }
                }

                // If this statement is confirmed, remove all errors. We dont care. 
                header.data.errorCount += errors.filter(e => e.level !== 'none').length; 
                header.data.errors = [...header?.data?.errors??[], ...errors]; 

                const cell = {
                    document: statement.document,
                    columnKey: statement.columnKey, 
                    rowKey: rowDef.key,
                    value: (statement[rowDef.key]??0),
                    data: {
                        calculated: statement.calculated?.[rowDef.key]??false, 
                        lowConfidence: lowConfidence,
                        confidence: confidence,
                        reconciles: statement.reconciles,
                        beginningBalance: statement.beginningBalance,
                        endingBalance: statement.endingBalance,
                        transactions: statement.tx,
                        accepted: statement.accepted,
                        previous: {
                            beginningBalance: previousStatement?.beginningBalance??0,
                            endingBalance: previousStatement?.endingBalance??0,
                        },
                        next: {
                            beginningBalance: nextStatement?.beginningBalance??0,
                            endingBalance: nextStatement?.endingBalance??0,
                        }
                    },
                    errors: errors,
                    canEdit: 
                        rowDef.key !== 'rowLabel' && 
                        rowDef.key !== 'total' && 
                        !isStatementLockedForCurrentUser &&
                        ((rowDef.key !== 'beginningBalance' && rowDef.key !== 'endingBalance') || !statement.split) &&
                        (!statement.diff?.[rowDef.key] || statement.diff?.[rowDef.key] === 0),
                    onEdit: async(value) => {
                        this.setState({disablingRefresh: true});

                        value = parseFloat(this.stringToFloatPrep(value));
                        
                        const statementValue = parseFloat(statement[rowDef.key]??0);

                        const statements = this.state.statements.map(s => {
                            if(s._id !== statement._id) return s; 
                            return {
                                ...s,
                                tx: {
                                    ...s.tx,
                                    [rowDef.key]: value
                                }
                            }
                        })

                        this.setState({statements});

                        // Now we need to either directly update the statement, or update the document... 
                        const updateDocument = [
                            'beginningBalance',
                            'deposits',
                            'income',
                            'withdrawals',
                            'fees',
                            'other',
                            'reinvestments',
                            'rollovers',
                            'loanPayments',
                            'transfers',
                            'gain_loss',
                            'endingBalance',
                            'accrued',
                        ].includes(rowDef.key);

                        if(updateDocument){
                            // Get the document. 
                            let document = await api.get(`/document/get?docId=${statement.document}`)
                            if(!document.success)
                                return toast.error("Failed to update the document.");
                            document = document.doc; 

                            // Get the original value from the document... 
                            let originalValue = document.metadata.find(m => m.key === metadataMapping[rowDef.key])?.value??"-";
                            if(originalValue === '-')
                                originalValue = '0'
                            originalValue = parseFloat(this.stringToFloatPrep(originalValue));

                            const newValue = originalValue + (value - statementValue); 

                            // Update the metadata of the document... 
                            const metadata = [
                                ...document.metadata.filter(m => m.key !== metadataMapping[rowDef.key]),
                                {
                                    key: metadataMapping[rowDef.key],
                                    value: this.formatValue(newValue).toString(),
                                    confidence: 1
                                }
                            ]

                            // Update the document with the new metadata... 
                            await api.post('/document/update', {
                                docId: statement.document,
                                docDiff: {metadata}
                            })
                        }
                        this.setState({disablingRefresh: false});
                    },
                }

                cell.onContextMenu = (e) => {
                    e.preventDefault(); 
                    this.openTableMenu(e, cell); 
                }

                return cell; 
            })

            // Add the statement cells to the accumalation... 
            return [...acc, ...statementCells]; 
        }, rowDefs.map(def => ({columnKey: 'rowLabel', rowKey: def.key, value: labelMapping[def.key]??'Subtype', data: {}, style:{color: 'var(--color-text)', fontWeight: 600}}))); // An intial array of cells for the row headers... 

        
        // Next we need to add cells in for the recievables... 
        
        

        // We have all statement data, and all row labels, we need to build the totals column. 
        rowDefs.forEach((rowDef) => {
            // Some keys we can ignore
            const ignore = ['rowLabel'];
            if(ignore.includes(rowDef.key))
                return;  

            let rowTotal = 0; 
            const rowCells = cells.filter(cell => cell.rowKey === rowDef.key && !ignore.includes(cell.columnKey));  
            if(rowDef.key === 'beginningBalance')
                rowTotal = rowCells[0]?.value;
            else if (rowDef.key === 'endingBalance')
                rowTotal = rowCells[rowCells.length - 1]?.value;
            else if (rowDef.key === 'balanceAccrued')
                rowTotal = rowCells[rowCells.length - 1]?.value;
            else 
                rowTotal = rowCells.reduce((acc, cell) => acc += cell.value, 0); 
            
            cells.push({
                columnKey: 'total',
                rowKey: rowDef.key, 
                value: (rowTotal??0),
                data: {} 
            });
        });

        // Now we can go through the headers, and set any context menu options we want.  
        headers = headers.map((header) => {
            let contextMenuOptions = [
                {
                    label: "Show transactions",
                    action: () => {
                        this.props.setFilter({
                            ...this.state.filters, 
                            statementId: header.data.statement, 
                        });
                        
                        document.getElementById('listable-rows').scrollIntoView({behavior: 'smooth'}); 
                    }
                },
                ...(((!header.data?.confirmed && this.props.user.email.endsWith('@stax.ai')) || (!header.data?.clientConfirmed && !this.props.user.email.endsWith('@stax.ai'))) ? [{
                    label: "Reclassify transactions",
                    action: async () => {
                        // First lets update the transactions... 
                        const transactions = this.state.transactions.map(t => {
                            if(t.statement !== header.data.statement) return t; 
                            return {
                                ...t, 
                                selected: false,
                                disabled: !t.creator,  
                            }
                        });

                        // Update the statement itself, and set classifing to true.  
                        const statements = this.state.statements.map(s => {
                            if(s._id !== header.data.statement) return s;
                            return {
                                ...s,
                                classifying: true
                            }
                        });

                        this.setState({transactions, statements});

                        // Next we need to make a request to actually reclassify the transactions... 
                        const res = await api.post('/tpa/transaction/classify', {
                            statementId: header.data.statement 
                        });

                        if(!res.success)
                            return toast.error("Oops, something went wrong while trying to reclassify transactions.");
                    }
                    }] : []),
                ...(((!header.data?.confirmed && this.props.user.email.endsWith('@stax.ai')) || (!header.data?.clientConfirmed && !this.props.user.email.endsWith('@stax.ai'))) ? [{
                    label: "Re-extract transactions",
                    action: async() => {
                        // Get the statement id we care about.
                        const statementId = header.data.statement;
                        const transactions = this.state.transactions.filter(t => {
                            return t.statement !== statementId || t.creator;
                        });
                        this.setState({transactions});

                        // Find the actual statement so we can get the document. 
                        const statement = this.state.statements.find(s => s._id === statementId);    
                        
                        const res = await api.post('/tpa/transaction/extract', {docId: statement.document}); 

                        if(res.success)
                            return toast.success("All of the transactions for this statement will be re-extracted shortly!")
                    }
                }] : [])
            ]; 

            if(header.data?.confirmed){
                contextMenuOptions.push({
                    label: "Unapprove",
                    action: async() => {
                        const res = await api.post('/tpa/statement/unconfirm', {
                            statementId: header.data.statement
                        });

                        if(!res.success)
                            return toast.error('Failed to unconfirm statement');

                        const statements = [...this.state.statements].map(s => {
                            if(s._id !== header.data.statement) return s; 
                            return {...s, confirmed: false, clientConfirmed: false}
                        });

                        this.setState({statements}); 
                    }
                })
            }

            if(!header.data?.statement)
                contextMenuOptions = [];

            
            return {...header, contextMenuOptions}; 
        })

        // We need to go through all the subtypes, and create the rowDefs, and cells.  
        Object.entries(subTypes).forEach(([txType, txSubTypes]) => {
            // Find the rowDef index, that has the same key as the type.
            const rowDefIndex = rowDefs.findIndex(def => def.key === txType); 
            if(rowDefIndex === -1)
                return; 

            // Set the rowDef style to be grayed out. 
            rowDefs[rowDefIndex].style = {color: 'var(--color-text-light)'};
            
            const subTypeTotals = {}; // Dict with the header, and total for all subtypes.

            Object.entries(txSubTypes).sort((a, b) => b[0].localeCompare(a[0])).forEach(([subType, months]) => {
                // We need to add the row in for the subType. 
                rowDefs.splice(rowDefIndex + 1, 0, {key: txType + subType});
            
                // Add in the label cell. 
                cells.push({
                    columnKey: 'rowLabel',
                    rowKey: txType + subType,
                    value: subType,
                    style: {paddingLeft: 20},
                    data: {}
                })

                let subTypeTotal = 0; 
                headers.forEach(header => {
                    if(!header.data)
                        return; 
                    
                    const column = header.columnKey;
                    const value = months[column]??0;
                    cells.push({
                        columnKey: column,
                        rowKey: txType + subType,
                        value: value,
                        data: {}
                    });
                    subTypeTotal += value; 

                    // Next we need to add this so we can calculate the difference between all subtypes and the total. 
                    if(!subTypeTotals[column])
                        subTypeTotals[column] = 0; 
                    subTypeTotals[column] += value;
                });

                // Add in the total cell for this subType. 
                cells.push({
                    columnKey: 'total',
                    rowKey: txType + subType,
                    value: subTypeTotal,
                    data: {}, 
                })
            })

            // Add in the row for 'Others' 
            rowDefs.splice(rowDefIndex + Object.keys(txSubTypes).length + 1, 0, {key: txType + 'Other'});
            cells.push({
                columnKey: 'rowLabel',
                rowKey: txType + 'Other',
                value: 'Other ' + txType,
                style: {paddingLeft: 20},
                data: {}
            });

            let OtherTotal = 0; 
            Object.entries(subTypeTotals).forEach(([column, value]) => {
                // Get the value of this type. 
                const typeValue = cells.find(cell => cell.rowKey === txType && cell.columnKey === column)?.value??0; 

                cells.push({
                    columnKey: column,
                    rowKey: txType + 'Other',
                    value: typeValue - value,
                    data: {}
                });
                OtherTotal += typeValue - value;
            });
            cells.push({
                columnKey: 'total',
                rowKey: txType + 'Other',
                value: OtherTotal,
                data: {}
            })
        })

        this.setState({cells, columnDefs, rowDefs, headers});
    }

    reconciles = (statement) => {
        if(statement.confirmed)
            return true; 

        const {beginningBalance:beginning, endingBalance:ending} = statement; 
        const {
            deposits, 
            withdrawals, 
            income, 
            fees, 
            transfers, 
            reinvestments,
            other, 
            gain_loss,
            loanPayments, 
            rollovers,
        } = statement.tx;

        for (const lhs of [
            // beginning + deposits + withdrawals + income + fees + other + transfers + gain + accrued,
            beginning + deposits + withdrawals + income + fees + other + transfers + gain_loss + reinvestments + loanPayments + rollovers,
        ]) {
            try {
                if (lhs.toFixed(2).replace('-0', '0') === ending.toFixed(2).replace('-0', '0'))
                    return true;
            } catch {}
        }
    
        return false; // Failed to reconcile
    }

    calculatedGainLoss = (endingBalance, beginningBalance, transactions) => {
        const {
            deposits, 
            withdrawals,
            income, 
            fees, 
            transfers, 
            other,
            reinvestments,
            loanPayments,
            rollovers,
            recievables, 
        } = transactions;

        return (endingBalance??0) - (
            (beginningBalance??0) +
            (deposits??0) +
            (withdrawals??0) +
            (income??0) +
            (fees??0) +
            (transfers??0) +
            (other??0) +
            (reinvestments??0) +
            (loanPayments??0) +
            (rollovers??0) +
            (recievables??0)
        );
    }


    // determine the statement from month and headers
    getStatementId = (month, headers) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']; 
        const getMonths = (monthRange) => {
            if (monthRange.includes("-")) {
                const [start, end] = monthRange.split(" - ");
                const startIndex = months.indexOf(start);
                const endIndex = months.indexOf(end);
                
                if (startIndex <= endIndex) {
                    return months.slice(startIndex, endIndex + 1).join(" - ");
                } else {
                    return [...months.slice(startIndex), ...months.slice(0, endIndex + 1)].join(" - ");
                }
            } else {
                return monthRange;
            }
        }

        // filter out the header data
        const filteredHeaders = headers.map(h => {
            if(h?.data?.statement ) {
                return {
                    statement: h.data.statement, 
                    month: h.columnKey,
                    mapping: getMonths(h.columnKey)
                };
            }
            return null;
        }).filter(Boolean)

        // find the corresponding statement
        const statement = filteredHeaders.find(h => h.mapping.includes(month))
        return statement?.statement
    }

    saveTransaction = async () =>{
        this.setState({isSaving: true});

        let transaction = {...this.state.editingTransaction}; 
        let transactions = [...this.state.transactions]; 
        
        let amount = parseFloat(transaction.amount);
        let res; 

        if(!transaction.txSubType || (transaction.txSubType && transaction.classificationRule)){
            const subtype = classifySubType(this.state.classificationRules || [], transaction);
            transaction.txSubType = subtype?.value;
            transaction.classificationRule = subtype?.rule?._id;
        }

        if(transaction._id === 'new'){            
            this.setState({loading: true});
            // If it is a new transaction, we need to create it first so we can get the _id.  
            const month = new Date(transaction.date).toLocaleDateString('en-US', {month: 'short'});;

            // get the statement Id of the statement the transaction is being added to
            const statementId = this.getStatementId(month, this.state.headers);
            const statement = this.state.statements.find(s => s._id === statementId);
            
            if (!statement) {
                this.setState({isSaving: false});
                return toast.error("Failed to find a statement for this transaction");
            }
            transaction.document = statement.document;
            transaction.statement = statement._id;
            delete transaction._id; 

            res = await api.post('/tpa/transaction/create', {transaction});

            transactions = [...transactions, {
                ...transaction, 
                _id: res.transaction._id,
                amount: amount, 
                creator: this.props.user._id, 
            }];

            this.setState({
                editingTransaction: null,
                transactions: transactions
            });

            this.setState({loading: false});
            return; 
        }
        else{
            transactions = transactions.map(t => {
                if(t._id !== transaction._id)
                    return t; 
                return {...t, ...transaction, amount: amount, creator: this.props.user._id};
            });

        this.setState({
            transactions,
            editingTransaction: null,
            isSaving: false, 
            typedInValue: "",
        }, () => {
            this.filterTransactions();
            this.formatToTable();
        });
            this.setState({
                editingTransaction: null,
                transactions: transactions
            });

            await api.post('/tpa/transaction/update', {
                transactionId: transaction._id,
                transactionDiff: {
                    ...transaction,
                    creator: this.props.user._id,
                }
            });
        }
       
    }

    editTransactionType = async (type) => {
        const updates = []; 
        const transactions = this.state.transactions.map(t => {
            if(!t.selected)
                return t; 

            const update = {
                ...t, 
                txType: type,
            }
            updates.push(update);
            return update; 
        });

        this.setState({transactions});

        await Promise.all(updates.map(t => {
            return api.post('/tpa/transaction/update', {
                transactionId: t._id,
                transactionDiff: {
                    txType: t.txType,
                    creator: this.props.user._id,
                }
            });
        }));
    }

    bulkEditType = async () => {
        this.props.modal("bulk-edit", {
            title: "Edit transaction type",
            message: "Select a new transaction type for the selected transactions",
            config: [
                {
                    key: 'txType',
                    type: 'select',
                    label: "Transaction Type",
                    options: [
                        {value: "Deposit", label: "Deposit"},
                        {value: "Fees", label: "Fees"},
                        {value: "Income", label: "Income"},
                        {value: "Withdrawal", label: "Withdrawal"},
                        {value: "Rollover", label: "Rollover"},
                        {value: "Reinvestment", label: "Reinvestment"},
                        {value: "Loan Payment", label: "Loan Payment"},
                        {value: "Transfer", label: "Transfer"},
                        {value: "Receivable", label: "Receivable"},
                        {value: "Other transaction", label: "Other transaction"},
                        {value: "Other", label: "-"},
                    ]
                }
            ],
            onSave: async (config) => {
                const txType = config.txType;
                if(!txType) return; 

                this.editTransactionType(txType);
            }
        })
    }

    bulkDelete = async () => {
        this.props.modal('confirm-caution', {
            message: <>
                If you delete these transactions, they will no longer be used or shown in the trust accounting sheet.
            </>,
            action: async() => {
                // Get all of the transactions that are selected...

                const delIds = [];
                const transactions = this.state.transactions.map(t => {
                    if(!t.selected)
                        return t;

                    delIds.push(t._id); 
                    return null; 
                }).filter(t => t !== null);

                this.setState({transactions: transactions}); 
                
                // Make a request to deledte the transactions...  
                api.post('/tpa/transaction/remove', {
                    transactionIds: delIds
                }); 
            }
        })
    }

    bulkDeselect = () => {
        const transactions = this.state.transactions.map(t => ({...t, selected: false}))
        this.setState({transactions: transactions, allTransactionsSelected: false});
    }

    renderTransactions = () => {

        // How many are selected? 
        const numSelected = this.state.transactions.filter(t => t.selected).length;
        const numHidden = this.state.transactions.length - (this.state.filteredTransactions.length - (this.state.editingTransaction?._id === 'new'? 1 : 0)); 

        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'sticky',
                        top: -1,
                        backgroundColor: 'var(--color-background)',
                        zIndex: 1,
                        marginTop: 30,
                        marginBottom: 20,
                    }}
                >
                    <h3 style={{fontWeight: 600}}>Transaction Detail</h3>
                    <div
                        className='a'
                        style={{
                            backgroundImage: 'url("https://static.stax.ai/images/icons/action-create.png")',
                            backgroundSize: 20,
                            backgroundRepeat: 'no-repeat',
                            backgroundPositionY: 'center',
                            paddingLeft: 25,
                            marginLeft: 20,
                        }}
                        onClick={() => {
                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']; 
                            // Check if we have a current filter for a statement, if we do, use that statements month. 
                            const statement = this.state.statements.find(s => s._id === this.state.filters.statementId); 
                            const month = statement? statement.months[statement.months.length - 1]: 'Jan';  

                            // Create a new transaction object. 
                            const date = new Date(this.props.planYear, months.indexOf(month), 1).toUTCString(); 

                            const newStatement = {
                                _id: 'new',
                                planYear: this.props.planYear, 
                                planId: this.state.plan.internalPlanId,
                                team: this.props.team._id,
                                accountNumber: this.state.account.number, 
                                date: date,
                                description: "",
                                amount: 0, 
                                txType: 'Other',
                                confidence: 1,
                            }

                            // Format the transaction date to mm/dd/yyyy and set as the typed date value. 
                            const formattedDate = new Date(newStatement.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                timeZone: 'UTC'
                            });
                            

                            // We need to add this new transaction to the filtered list (This ensures it is shown).
                            this.setState({
                                editingTransaction: {...newStatement}, 
                                typedInValue: formattedDate 
                            }, () => this.filterTransactions());
                        }}
                    >Transaction</div>
                    {this.props.team.scopes?.includes("TPAClassificationRules") &&
                    <div
                        className='a'
                        style={{
                            backgroundImage: 'url("https://static.stax.ai/images/icons/action-create.png")',
                            backgroundSize: 20,
                            backgroundRepeat: 'no-repeat',
                            backgroundPositionY: 'center',
                            paddingLeft: 25,
                            marginLeft: 20,
                        }}
                        onClick={() => {
                            this.props.modal("classification-rule", {
                                transactions: this.state.transactions, 
                                subTypes: this.state.subTypes,
                                rules: this.state.classificationRules || [],
                                account: this.state.account,
                                plan: this.state.plan,
                                removeRule: async(rule) => {
                                    const rules = (this.state.classificationRules || []).filter(r => r._id !== rule._id); 
                                    const transactions = this.state.transactions.map(t => {
                                        if(t.txSubType && !t.classificationRule)
                                            return t; 
                                        const subtype = classifySubType(rules, t);
                                        return {
                                            ...t, 
                                            txSubType: subtype?.value, 
                                            classificationRule: subtype?.rule?._id
                                        };
                                    })

                                    this.setState({classificationRules: rules, transactions}); 
                                    await api.post('/tpa/transaction/rules/remove', {ruleId: rule._id});
                                },
                                saveRule: async(rule) => {
                                    const endpoint = rule._id? '/tpa/transaction/rules/update' : '/tpa/transaction/rules/create';
                                    const body = rule._id? {ruleId: rule._id, update: rule} : {rule};
                                    const res = await api.post(endpoint, body); 
                                    if(!res.success)
                                        return toast.error("Failed to save classification rule.");  

                                    let classificationRules = [...(this.state.classificationRules || [])];
                                    if(rule._id){
                                        classificationRules = classificationRules.map(r => {
                                            if(r._id !== rule._id)
                                                return r; 
                                            return res.rule; 
                                        })
                                    }
                                    else{
                                        classificationRules.push(res.rule); 
                                    }
                                    const transactions = this.state.transactions.map(t => {
                                        if(t.txSubType && !t.classificationRule)
                                            return t;  

                                        const subtype = classifySubType(classificationRules, t);  
                                        return {
                                            ...t, 
                                            txSubType: subtype?.value, 
                                            classificationRule: subtype?.rule?._id
                                        };
                                    })

                                    this.setState({classificationRules, transactions});
                                }, 
                                onClose: (rules) => {
                                    this.props.modal(); 
                                }
                            })
                        }}
                    >Classification Rule</div>
                    }
                    {
                        numSelected > 1? 
                        <>
                                <div
                                style={{
                                    display: 'flex',
                                    marginLeft: 25,
                                    color: 'var(--color-text-light)'
                                }}
                            >
                                <>{numSelected}/{this.state.filteredTransactions.length} selected · </>
                                <ActionMenu 
                                    label={<span 
                                        style={{
                                            color: 'var(--color-primary-dark)',
                                            cursor: 'pointer',
                                            whiteSpace: 'pre'
                                        }}
                                        > Bulk actions</span>}
                                    actions={[
                                        {
                                            label: <>Edit type <span style={{color: 'var(--color-text-light)', paddingLeft: 10}}>Ctrl + P</span></>,
                                            action: this.bulkEditType
                                        },
                                        {
                                            label: 'Edit sub-type',
                                            action: () => {
                                                this.props.modal("bulk-edit", {
                                                    title: "Edit transaction type",
                                                    message: "Select a new transaction type for the selected transactions",
                                                    config: [
                                                        {
                                                            key: 'txSubType',
                                                            type: 'creatable',
                                                            label: "Transaction SubType",
                                                            options: [
                                                                ...this.state.transactions.reduce((acc, t) => {
                                                                    if(!t.txSubType) return acc; 
                                                                    if(acc.find(a => a.value === t.txSubType)) return acc;
                                                                    return [...acc, {label: t.txSubType, value: t.txSubType}]
                                                                }, [])
                                                            ]
                                                        }
                                                    ],
                                                    onSave: async (config) => {
                                                        // Get the TxType
                                                        const txSubType = config.txSubType;
                                                        if(!txSubType) return; 

                                                        const updates = []; 
                                                        const transactions = this.state.transactions.map(t => {
                                                            if(!t.selected)
                                                                return t; 

                                                            const update = {
                                                                ...t, 
                                                                txSubType: txSubType,
                                                            }
                                                            updates.push(update);
                                                            return update; 
                                                        });

                                                        this.setState({transactions});

                                                        await Promise.all(updates.map(t => {
                                                            return api.post('/tpa/transaction/update', {
                                                                transactionId: t._id,
                                                                transactionDiff: {
                                                                    txSubType: t.txSubType,
                                                                    creator: this.props.user._id,
                                                                }
                                                            });
                                                        }));
                                                    }
                                                })
                                            }
                                        },
                                        {
                                            label: 'Delete all',
                                            action: this.bulkDelete
                                        },
                                        {
                                            label: 'Deselect all',
                                            action: this.bulkDeselect
                                        }
                                    ]}
                                />

                                {
                                    (() => {
                                           // Do we have any selected transactions? Sum up the total...  
                                        const selected = this.state.filteredTransactions.filter(t => t.selected);
                                        const total = selected.reduce((acc, t) => {
                                            return acc += parseFloat(t.amount);
                                        }, 0);
                                        return (
                                            <div
                                                style={{
                                                    color: 'var(--color-text-light)',  
                                                    paddingLeft: 20,
                                                }}
                                            >{`Amount: ${total.toLocaleString()}`}</div>
                                        )
                                    })()
                                }
                            </div>
                        </>:""
                    }

                    {
                        numHidden > 0? 
                        <div style={{
                            marginLeft: 25,
                            color: 'var(--color-text-light)'
                        }}>{numHidden} Transactions hidden</div>:""
                    }
                    {
                        <div
                            className='a'
                            style={{marginLeft: 25}}
                            onClick={() => {
                                // Scroll to the bottom of the page... 
                                // Get the main content element... 
                                this.state.scroll.action(); 
                            }}
                        >
                            {this.state.scroll.label}
                        </div> 
                    }
                    <div style={{marginLeft: 'auto'}}/>
                    <Filter 
                        label='Filter Transactions'
                        onClick={() => {
                            this.props.modal('filter-transactions', {transactions: this.state.transactions, filterAccounts: this.filterAccounts})
                        }}
                        filterCount={Object.entries({...this.state.filters}).filter(f => {
                                const [key, value] = f;

                                // Check if f is not null
                                if (value !== null) {
                                    // Check if f is an array and has a length greater than 0
                                    if (Array.isArray(value) && value.length > 0) {
                                        return true;
                                    }
                                    // Check if f is an object (but not an array, as arrays are also objects)
                                    else if (typeof value === 'object' && !Array.isArray(value)) {
                                        // if the key is pye and the date is 12/31, don't count it
                                        if ((key === 'pye' || key === 'settleDate' ) && value.from.endDd === 1 && value.from.endMm === 1 && value.to.endDd === 31 && value.to.endMm === 12) {
                                            return false;
                                        } 
                                        // Check if the object has at least one property set
                                        return Object.values(value).some(val => val !== null && val !== undefined && val !== '');
                                    }
                                    else if( typeof value === 'string' && value.length)
                                        return true;
                                }
                                return false;
                            }).length
                        } 
                        onClear={async() => {
                            await this.props.clearFilters();
                        }}
                    />
                </div>
                <Listable 
                    columns={[
                        {
                            header: (
                                <Checkbox 
                                    checked={this.state.allTransactionsSelected}
                                    onToggle={() => {
                                        const filteredTransactions = this.state.filteredTransactions.map(t => t._id);
                                        const transactions = this.state.transactions.map(t => {
                                            if(!filteredTransactions.includes(t._id)) return t;  
                                            return {
                                                ...t, 
                                                selected: !this.state.allTransactionsSelected && !t.disabled, 
                                            }
                                        });

                                        this.setState({transactions, allTransactionsSelected: !this.state.allTransactionsSelected});
                                    }}
                                />
                            ),
                            prop: "selected",
                            width: 20,
                            align: 'center',
                            grow: false, 
                            disableSort: true, 
                            render: (item) => {
                                return (
                                    <div style={{display: 'flex', alignItems: 'center', height: 38}}>
                                        <Checkbox 
                                            checked={item.selected}
                                            onToggle={(e) => {
                                                if(item.disabled)
                                                    return; 

                                                let transactions = [...this.state.transactions]; 
                                                transactions = transactions.map(t => {
                                                    if(t._id === item._id)
                                                        return {...t, selected: !t.selected};
                                                    return t;
                                                });
                                            
                                                this.setState({transactions});
                                            }}
                                        />
                                    </div>
                                   
                                )
                            }
                        },
                        {
                            header: <div style={{color: 'var(--color-text-light)', fontWeight: 500}}>Settle Date</div>,
                            prop: "date",
                            width: 130,
                            align: 'left',
                            grow: false, 
                            overflow: true,
                            render: (item) => {
                                const isEditing = this.state.editingTransaction?._id === item._id;

                                const date = isEditing? this.state.editingTransaction?.date: item.date;  
                                const formattedDate = date? new Date(date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    timeZone: 'UTC'
                                }):"-";

                                return (
                                    this.state.editingTransaction?._id === item._id? 
                                    <div 
                                        style={{display: 'flex', flexDirection: 'column'}}
                                        onClick={() => {
                                            this.setState({showDatePicker: true});
                                        }}
                                    >
                                        <input 
                                            className="investments-transactions-select-edit investments-transactions-input-container"
                                            type={"text"}
                                            ref={this.inputRef}
                                            onChange={async(e) => {
                                                const typedValue = e.target.value;

                                                this.setState({
                                                    typedInValue: typedValue
                                                });
                                            }}
                                            onKeyDown={async (e) => {
                                                if (e.key === "Enter") {
                                                    e.target.blur();
                                                    const inputDate = this.state.typedInValue;

                                                    try {
                                                        if (inputDate === "-" || inputDate === "" || inputDate === " ") {
                                                            this.setState({
                                                                showDatePicker: false,
                                                                editingTransaction: {...this.state.editingTransaction, date: null}
                                                            }, async () => {
                                                                if (item._id !== "new") await this.saveTransaction(item);
                                                            })
                                                            return;
                                                        } 
                                                        const date = new Date(inputDate);

                                                        if (!isNaN(date)) {
                                                            this.setState({
                                                                showDatePicker: false,
                                                                editingTransaction: {...this.state.editingTransaction, date: date.toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'numeric',
                                                                    day: 'numeric',
                                                                }).replace("2001", this.props.planYear)}
                                                            }, async () => {
                                                                if (item._id !== "new") await this.saveTransaction(item);
                                                            })

                                                        } else {
                                                            toast.error("Please enter a valid date or type '-' to unset the date.");
                                                            return;
                                                        }
                                                    } catch (error) {
                                                        toast.error("Error parsing date:", error.message);
                                                        return;
                                                    }                                                        
                                                }
                                                // Add event listener for up and down arrow keys. If up, increase date by one, if down, decrease date by one.  
                                                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                                    const date = new Date(this.state.editingTransaction.date);
                                                    const newDate = new Date(date.setDate(date.getDate() + (e.key === "ArrowUp" ? 1 : -1)));
                                                    const formattedDate = newDate.toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        timeZone: 'UTC'
                                                    });
                                                    this.setState({
                                                        editingTransaction: {...this.state.editingTransaction, date: newDate}, 
                                                        typedInValue: formattedDate
                                                    })
                                                } 
                                            }}
                                            value={this.state.typedInValue}
                                        />
                                        {this.state.showDatePicker && 
                                        <DismissContainer
                                            onClose={() => this.setState({showDatePicker: false})}
                                            noAbsolute={true}
                                        >
                                            <div style={{zIndex: 99, marginTop: -11}}>
                                                <Calendar 
                                                    className='date-picker'
                                                    showMonthAndYearPickers={true}
                                                    date={new Date(formattedDate)}
                                                    months={1}
                                                    direction="horizontal"
                                                    editableDateInputs={true}
                                                    onChange={(value) => {
                                                        // Format the date, and update the typed in value. 
                                                        const formattedDate = value.toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            timeZone: 'UTC'
                                                        });

                                                        this.setState({
                                                            showDatePicker: false,
                                                            editingTransaction: {...this.state.editingTransaction, date: value},
                                                            typedInValue: formattedDate 
                                                        })
                                                    }}
                                                    onShownDateChange={(value) => {
                                                        const formattedDate = value.toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            timeZone: 'UTC'
                                                        });

                                                        this.setState({
                                                            // showDatePicker: false,
                                                            editingTransaction: {...this.state.editingTransaction, date: value},
                                                            typedInValue: formattedDate 
                                                        })
                                                    }}
                                                /> 
                                            </div>
                                        </DismissContainer>
                                        }
                                    </div>: 
                                    <Highlight text={formattedDate} match={this.props.query}/>
                                )
                            }
                        },
                        {
                            header: "Type",
                            prop: "txType",
                            width: 200,
                            grow: false,
                            overflow: true, 
                            render: (item) => {
                                return (
                                    this.state.editingTransaction?._id === item?._id?
                                        <Select 
                                            className={"investments-transactions-select-container"}
                                            classNamePrefix={"filter-select"}
                                            maxMenuHeight={100}
                                            isMulti={false}
                                            isClearable={false}
                                            options={[
                                                {value: "Deposit", label: "Deposit"},
                                                {value: "Fees", label: "Fees"},
                                                {value: "Income", label: "Income"},
                                                {value: "Withdrawal", label: "Withdrawal"},
                                                {value: "Rollover", label: "Rollover"},
                                                {value: "Reinvestment", label: "Reinvestment"},
                                                {value: "Loan Payment", label: "Loan Payment"},
                                                {value: "Transfer", label: "Transfer"},
                                                {value: "Receivable", label: "Receivable"},
                                                {value: "Other transaction", label: "Other transaction"},
                                                {value: "Other", label: "-"},
                                            ]}
                                            onChange={(value) => {
                                               this.setState({editingTransaction: {...this.state.editingTransaction, txType: value.value}}, () => {
                                                    if(this.state.editingTransaction._id === 'new')
                                                        return;
                                                    this.saveTransaction(); 
                                               });
                                            }}
                                            value={{label: this.state.editingTransaction?.txType === 'Other'? "-": this.state.editingTransaction?.txType, value: this.state.editingTransaction?.txType}}
                                        />
                                  
                                    :
                                    <Highlight text={item.txType === 'Other'? "-": item.txType} match={this.props.query}/>
                                );
                            }
                        },
                        {
                            header: "Sub-type",
                            prop: "txSubType",
                            width: 200,
                            overflow: true,
                            grow: false,
                            render: (item) => {
                                const classificationRule = this.state.classificationRules?.find(r => r._id === item.classificationRule); 
                                return (
                                    this.state.editingTransaction?._id === item?._id?
                                    <CreatableSelect
                                        className={"investments-transactions-select-container"}
                                        classNamePrefix={"filter-select"}
                                        isMulti={false}
                                        isClearable={false}
                                        autoBlur={false}
                                        maxMenuHeight={100}
                                        value={{label: this.state.editingTransaction?.txSubType, value: this.state.editingTransaction?.txSubType}}
                                        options={[
                                            ...((this.state.subTypes[item?.txType]??[]).map(subType => ({value: subType, label: subType}))),
                                            {value: "", label: "-"}
                                        ]}
                                        onChange={(value) => {
                                            this.setState({editingTransaction: {...this.state.editingTransaction, txSubType: value.value, classificationRule: null}}, () => {
                                                if(this.state.editingTransaction._id === 'new')
                                                    return;
                                                this.saveTransaction();
                                            });

                                            // Does this value exist in subTypes? If not add it. 
                                            if(value.value && !this.state.subTypes[item?.txType]?.includes(value.value)){
                                                this.setState({
                                                    subTypes: {
                                                        ...(this.state.subTypes??{}),
                                                        [item.txType]: [...(this.state.subTypes[item.txType]??[]), value.value]
                                                    }
                                                })
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if(e.key === 'Enter'){
                                                this.setState({editingTransaction: {...this.state.editingTransaction, txSubType: e.target.value, classificationRule: null}});
                                            }
                                        }}
                                    />
                                    :
                                    <>
                                    <Highlight text={item?.txSubType} match={this.props.query}/>
                                    {classificationRule?._id && <div className='investments-transaction-classified-tag' data-classified-by-rule='true' title={`Classified by rule: ${classificationRule.name}`}/>}
                                    </>
                                );
                            }
                                
                        },
                        {
                            header: "Description",
                            prop: "description",
                            width: 100,
                            render: (item) => {
                                return (
                                    this.state.editingTransaction?._id === item?._id?
                                    <input 
                                        className="investments-transactions-select-edit"
                                        type={"text"}
                                        value={this.state.editingTransaction?.description}
                                        onChange={(e) => this.setState({editingTransaction: {...this.state.editingTransaction, description: e.target.value}}) }
                                        placeholder={"Description..."}
                                        onKeyDown={(e) => {
                                            if(e.key === 'Enter' && this.state.editingTransaction._id !== 'new'){
                                                this.saveTransaction();
                                            }
                                        }}
                                    />
                                    :
                                    <div title={item.description}><Highlight text={item.description} match={this.props.query}/></div>
                                )
                            }
                        },
                        {
                            header:"Amount",
                            prop: "amount",
                            align: 'right',
                            grow: false, 
                            width: 120,
                            render: (item) => {
                                const amount = parseFloat(item.amount);
                                return (
                                    this.state.editingTransaction?._id === item?._id ?
                                    <input 
                                        className="investments-transactions-select-edit investments-transactions-input-container investment-transactions-input-amount-container"
                                        type={"text"}
                                        value={this.state.editingTransaction?.amount}
                                        onKeyDown={(e) => {
                                            if(e.key === 'Enter' && this.state.editingTransaction._id !== 'new'){
                                                this.saveTransaction();
                                            }
                                        }}
                                        onChange={(e) => {
                                            const newValue = e.target.value;

                                            if (newValue === '' || /^-?\d*\.?\d*$/.test(newValue)) {
                                                this.setState({
                                                    editingTransaction: {
                                                        ...this.state.editingTransaction,
                                                        amount: newValue
                                                    }
                                                });
                                            }
                                        }}
                                        placeholder={"Amount..."}
                                    />
                                    :
                                    <Highlight 
                                        text={amount ? amount < 0 ? `(${(amount * -1).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`: amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }):"-"}
                                        match={this.props.query}
                                    />
                                );
                            }
                        },
                        {
                            header: "",
                            prop: "edit",
                            width: 230,
                            align: 'right',
                            grow: false, 
                            render: (item) => {
                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'end',
                                            gap: 20,
                                        }}
                                    >
                                        {
                                            this.state.editingTransaction?._id === item?._id ?
                                            <>
                                                <div
                                                    className='a investments-transactions-edit'
                                                    style={{
                                                        backgroundImage: 'url("https://static.stax.ai/images/icons/confirm.png")',
                                                    }}
                                                    onClick={this.saveTransaction}
                                                >
                                                    Save
                                                </div>
                                                <div
                                                    className='a investments-transactions-edit'
                                                    style={{
                                                        backgroundImage: 'url("https://static.stax.ai/images/icons/delete.png")',
                                                        color: 'var(--color-accent-dark)'
                                                    }}
                                                    onClick={() => {
                                                        this.setState({
                                                            editingTransaction: null,
                                                        }, () => this.filterTransactions());
                                                    }}
                                                >
                                                    Cancel
                                                </div>
                                            </>
                                            :
                                            <>
                                                <div
                                                    className='a investments-transactions-edit'
                                                    style={{
                                                        backgroundImage: 'url("https://static.stax.ai/images/icons/action-open.png")',
                                                    }}
                                                    onClick={() => {
                                                        const statement = this.state.statements.find(s => s._id === item.statement); 
                                                        window.open(`/document/${statement?.document??""}?page=${item.page??0}`, "",  '_blank')
                                                    }}
                                                >
                                                    Open
                                                </div>
                                                <div
                                                    className={`a investments-transactions-edit ${((item.confirmed && this.props.user.email.endsWith('@stax.ai')) || (item.clientConfirmed && !this.props.user.email.endsWith('@stax.ai'))) ? 'disabled' : ''}`}
                                                    style={{
                                                        backgroundImage: 'url("https://static.stax.ai/images/icons/edit.png")'
                                                    }}
                                                    onClick={() => {
                                                        if((!item.confirmed && this.props.user.email.endsWith('@stax.ai')) || (!item.clientConfirmed && !this.props.user.email.endsWith('@stax.ai'))){
                                                        const transactions = this.state.transactions.filter(t => t._id !== 'new');
                                                        this.setState({
                                                            transactions: transactions, 
                                                            editingTransaction: {...item}
                                                        })}
                                                        else{
                                                            toast.error("This transaction is locked. Please unapprove the statement to edit this transaction.")
                                                        }
                                                    }}
                                                >
                                                    Edit
                                                </div>
                                                <div
                                                    className='a investments-transactions-edit'
                                                    style={{
                                                        backgroundImage: 'url("https://static.stax.ai/images/icons/delete.png")',
                                                        color: 'var(--color-accent-dark)'
                                                    }}
                                                    onClick={() => {
                                                        if((!item.confirmed && this.props.user.email.endsWith('@stax.ai')) || (!item.clientConfirmed && !this.props.user.email.endsWith('@stax.ai'))){
                                                        this.props.modal('confirm-caution', {
                                                            message: <>
                                                                If you delete this transaction, it will no longer be used or shown in the trust accounting sheet.
                                                            </>,
                                                            action: async() => {
                                                                this.setState({
                                                                    transactions: this.state.transactions.filter(t => t._id !== item._id), 
                                                                });
        
                                                                const res = await api.post('/tpa/transaction/remove', {
                                                                    transactionId: item._id
                                                                });
                                                                if(!res.success)
                                                                    return toast.error("Failed to delete the transaction");
                                                            }
                                                        })}
                                                        else{
                                                            toast.error("This transaction is locked. Please unapprove the statement to delete this transaction.")
                                                        }
                                                    }}
                                                >
                                                    Delete
                                                </div>
                                            </>
                                        }
                                    </div>
                                )
                            }
                        },
                        {
                            header: '',
                            prop: "icon",
                            width: 28,
                            overflow: true,
                            grow: false,
                            render: (item) => {
                                const renderError = (errors) => {
                                    return (
                                        <div
                                            className='transaction-error-menu'
                                        >
                                            <div
                                                className='transaction-error-container'
                                            >
                                                {
                                                    errors.map(error => {
                                                        return (
                                                            <>
                                                                <h1 className='transaction-error-title'>{error.title}</h1>
                                                                <div className='transaction-error-description'>{error.description}</div>
                                                                <div
                                                                    className='transaction-error-action-container'
                                                                >
                                                                    {
                                                                        error.actions?.map((action, i) => {
                                                                            return (
                                                                                <div
                                                                                    onClick={action.action}
                                                                                    className='transaction-error-action'
                                                                                >
                                                                                    {action.label}
                                                                                </div>
                                                                            )
                                                                        })
                                                                    }
                                                                </div>
                                                            </>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </div>
                                    )
                                }

                                const renderUser = () => {
                                    let userName = (this.props.team?.users.find(user=>user._id === item.creator))?.name

                                    return (
                                        <div 
                                            className="document-thumbnail-icon-assign"
                                            style={{
                                                position: 'static',
                                                marginTop: 5,
                                            }}
                                            title={`This transaction was modified by ${userName}`}
                                        >
                                            {userName ? userName[0] : "M"}
                                        </div>
                                    );
                                }

                                return (
                                    <>
                                        {item.errors?.length && !item.creator?
                                            renderError(item.errors):''
                                        }
                                        {item.creator?
                                            renderUser():''
                                        }
                                    </>
                                );
                            }
                        },
                    ]}
                    data={[...this.state.filteredTransactions]}
                    debug={false}
                    emptyMessage={"No transactions..."}
                    onRowDoubleClick={(item) => {
                        if((!item.confirmed && this.props.user.email.endsWith('@stax.ai')) || (!item.clientConfirmed && !this.props.user.email.endsWith('@stax.ai'))){
                            if(this.state.editingTransaction?._id === item._id)
                                return;


                            // If the item does not have a date, set one. 
                            if(!item.date){
                                // Set the date to Jan 1st of the selected year. 
                                item.date = new Date(this.props.planYear, 0, 1).toUTCString(); 
                            }


                            // Format the transaction date to mm/dd/yyyy and set as the typed date value. 
                            const date = new Date(item.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                timeZone: 'UTC'
                            });

                            this.setState({
                                editingTransaction: {...item}, 
                                typedInValue: date 
                            })
                        } else{
                            toast.error("This transaction is locked. Please unapprove the statement to edit this transaction.")
                        }
                    }}
                    onRowClick={(item, e) => {
                        e.stopPropagation(); 
                        const isCntrl = e.nativeEvent.ctrlKey || e.nativeEvent.metaKey; 
                        if(!isCntrl)
                            return;  

                        const currentIdx = this.state.filteredTransactions.findIndex(t => t._id === item._id); 
                        const firstSelectedIdx = this.state.filteredTransactions.reduce((acc, t, idx) => {
                            if(t.selected && Math.abs(idx - currentIdx) < Math.abs(acc - currentIdx))
                                return idx;
                            return acc;
                        }, 0);

                        // Get the filtered ids between first and current, 
                        const filteredTransactions = this.state.filteredTransactions.slice(Math.min(firstSelectedIdx, currentIdx), Math.max(firstSelectedIdx, currentIdx)).map(t => t._id);

                        const transactions = this.state.transactions.map(t => {
                            if(filteredTransactions.includes(t._id) || t._id === item._id)
                                return {
                                    ...t, 
                                    selected: !t.disabled, 
                                }
                            
                            return t; 
                        });

                        this.setState({transactions});
                    }}
                    onSort={(items) => {
                        this.setState({filteredTransactions: items});
                    }}
                    contextMenuOptions={[

                        {
                            label: 'Copy value',
                            action: (item) => {
                                // Write the value to clipboard... 
                                navigator.clipboard.writeText(item.copyValue??"");
                                toast.success("Copied to clipboard!");
                            }
                        },
                        {
                            label: "Flip sign",
                            action: async (item) => {
                                const transactions = this.state.transactions.map(t => {
                                    if(t._id !== item._id)
                                        return t; 
                                    return {
                                        ...t, 
                                        amount: t.amount * -1
                                    }
                                })
                                this.setState({transactions});

                                // Make a request to update the transaction... 
                                await api.post('/tpa/transaction/update', {
                                    transactionId: item._id, 
                                    transactionDiff: {
                                        amount: item.amount * -1,
                                    }
                                })
                            },
                        },
                    ]}
                />
            </>
        )
    }

    renderWarnings = () => {
        if(this.state.loading)
            return null; 
        // Get all of the statements... 
        const statements = [...this.state.statements]; 
        // Are we missing any months? 
        let missing = this.state.expectedCoverage??[];

        let failedReconciles = [];
        statements.forEach(statement => {
            // Get the months this statement covers... 
            const months = statement.months; 
            months.forEach(month=> {
                if(missing.includes(month))
                    missing.splice(missing.indexOf(month), 1);
            });

            if(!statement.reconciles)
                failedReconciles.push(months[months.length -1]);
        });

        // Remove any duplicates...
        missing = [...new Set(missing)];
        failedReconciles = [...new Set(failedReconciles)];
        
        let missingCount = writtenNumber(missing.length);
        missingCount = missingCount.charAt(0).toUpperCase() + missingCount.slice(1);

        let failedReconcilesCount = writtenNumber(failedReconciles.length); 
        failedReconcilesCount = failedReconcilesCount.charAt(0).toUpperCase() + failedReconcilesCount.slice(1);

        return (
            <>
                {missing.length?
                    <Warning 
                        title='Missing statements'
                        message={<><span style={{fontWeight: 600}}>{missingCount}</span> statements ({missing.join(', ')}) are missing for this account. Use the 'Find missing' buttons below each month in the <span style={{fontWeight: 600}}>Trust Accounting</span> section to search for these statements. If you find physical copies of the statement, please upload them to Stax,ai and wait for them to run though the pipeline.</>}
                    />:''
                }
                {
                    failedReconciles.length?
                    <Warning 
                        title='Possible error in extraction'
                        message={<><span style={{fontWeight: 600}}>{failedReconcilesCount}</span> statements ({failedReconciles.join(', ')}) did not pass our internal gain/(loss) validation check. Please open the statement(s), check, and fix the incorrect extractions in the document's fields.</>}
                    />:''
                }
            </>
        )
    }

    renderAccountInfo = () => {

        const accountTypes = {
            brokerage: 'Brokerage',
            rk: 'Record-keeper',
            ignore: 'Ignore'
        }

        return (
            <div>
                <div
                    style={{display: 'inline-flex'}}
                >
                    <h3 style={{fontWeight: 600}}>Account Information</h3>
                    {
                        this.state.editingAccount?._id && 
                        <>
                            <div 
                                className='account-info-save a'
                                onClick={async() => {
                                    this.setState({editingAccount: null});
                                    await api.post('/tpa/account/update', {
                                        id: this.state.account._id,
                                        accountDiff: {
                                            ...this.state.account
                                        }
                                    });
                                }}
                            >
                                Save
                            </div>
                            <div 
                                className='account-info-cancel'
                                onClick={() => {
                                    this.setState({account: {...this.state.editingAccount}, editingAccount: null});
                                }}
                            >
                                Cancel
                            </div>
                        </>
                    }
                </div>

                <div
                    className='account-info'
                > 
                    <div
                        className='account-info-details'
                    >
                        <div
                            className='account-info-detail provider'
                        >
                            <label>Investment Provider</label>
                            <CreatableSelect 
                                className="provider-select-container"
                                classNamePrefix='provider-select'
                                value={{label: this.state.account?.provider??"", value: this.state.account.provider??""}}
                                options={this.state.investmentProviders?.map(p => ({label: p.DisplayName, value: p.DisplayName}))}
                                onChange={async(e) => {
                                    // Is this the first change? set editing account so we have a backup...
                                    if(!this.state.editingAccount)
                                        this.setState({editingAccount: {...this.state.account}});

                                    const account = {
                                        ...this.state.account, 
                                        provider: e.value
                                    }
                                    this.setState({account});    
                                }}
                            />
                        </div>
                        <div
                            className='account-info-detail number'
                        >
                            <label>Account Number</label>
                            <input 
                                type='text'
                                className='account-info-detail-input'
                                value={this.state.account.number}
                                onChange={e => {
                                    // Is this the first change? set editing account so we have a backup...
                                    if(!this.state.editingAccount)
                                        this.setState({editingAccount: {...this.state.account}});

                                    const account = {
                                        ...this.state.account,
                                        number: e.target.value
                                    }
                                    this.setState({account})
                                }}
                            />
                        </div>
                        <div
                            className='account-info-detail participant'
                        >
                            <label>FBO/Participant</label>
                            <input 
                                type='text'
                                className='account-info-detail-input'
                                value={this.state.account.payee}
                                onChange={e => {
                                    // Is this the first change? set editing account so we have a backup...
                                    if(!this.state.editingAccount)
                                        this.setState({editingAccount: {...this.state.account}});
                                    const account = {
                                        ...this.state.account,
                                        payee: e.target.value
                                    }
                                    this.setState({account})
                                }}
                            />
                        </div>
                        <div
                            className='account-info-detail type'
                        >
                            <label>Account Type</label>
                            <CreatableSelect 
                                className="provider-select-container"
                                classNamePrefix='provider-select'
                                value={{label: accountTypes[this.state.account.accountType]??this.state.account.accountType, value: this.state.account.accountType}}
                                options={Object.entries(accountTypes).map(([key, value]) => ({label: value, value: key}))}
                                onChange={async(e) => {
                                    // Is this the first change? set editing account so we have a backup...
                                    if(!this.state.editingAccount)
                                        this.setState({editingAccount: {...this.state.account}});
                                    const account = {
                                        ...this.state.account, 
                                        accountType: e.value
                                    }
                                    this.setState({account});    
                                }}
                            />
                        </div>
                    </div>

                    <div
                        className='account-info-notes'
                    >
                        <textarea 
                            placeholder="No notes..."
                            value={this.state.account.notes??""}
                            onChange={(e) => {
                                this.setState({account: {...this.state.account, notes: e.target.value}});
                            }}
                            onKeyDown={(e) => {
                                if(e.key === 'Enter')
                                    e.target.blur();
                                if(e.key === 'Escape')
                                    e.target.blur(); 
                            }}
                            onBlur={(e) => {
                                api.post('/tpa/account/update', {
                                    id: this.state.account._id,
                                    accountDiff: {
                                        notes: e.target.value
                                    }
                                });
                            }}
                        />
                    </div>
                </div>
                <div>
                    <Checkbox 
                        className='account-info-checkbox'
                        checked={this.state.account.viewed?.[this.props.planYear]??false}
                        onToggle={(e) => {
                            const account = {
                                ...this.state.account, 
                                viewed: {
                                    ...this.state.account.viewed??{}, 
                                    [this.props.planYear]: this.state.account.viewed?.[this.props.planYear]? null: this.props.user._id
                                }
                            }
                            this.setState({account});
                            api.post('/tpa/account/update', {
                                id: this.state.account._id,
                                accountDiff: {
                                    viewed: account.viewed
                                }
                            })
                            
                            if (this.props.user?.email?.endsWith("@stax.ai")) {
                                // Potentially a Stax.ai admin team member - record split document action
                                api.post('/internal/record', {
                                    user: this.props.user.email,
                                    team: this.props.team.name,
                                    code: "Review",
                                    points: 2,
                                });
                            }
                        }}
                        label="Reviewed"
                    />
                </div>
            </div>

        )
    }

    renderTrustAccounting = () => {
        // We have everything we should need. Render the table.  

        const getContextMenuOptions = (cell) => {
            const options = [
                {
                    label: "Copy value",
                    action: (cell) => {
                        // Copy the value of the cell. 
                        navigator.clipboard.writeText(this.floatToStringFormatted(cell.value??""));
                        toast.success("Copied to clipboard!");
                    }
                }, 
                {
                    label: "Journal to",
                    action: (cell) => {
                        // here we need to do this for both the client and the internal user! 
                        const confirmed = this.state.statements.find(s => s?.document === cell?.document)?.confirmed
                        const clientConfirmed = this.state.statements.find(s => s?.document === cell?.document)?.clientConfirmed

                        if((confirmed && this.props.user.email.endsWith('@stax.ai')) || (clientConfirmed && !this.props.user.email.endsWith('@stax.ai')))
                            return toast.error("This statement is approved. Please unapprove the statement to make changes.");
    
                        const month = cell.columnKey; 
                        const source = cell.rowKey; 
                        const amount = cell.value; 
                        this.props.modal('journal-to', {
                            month: month,
                            source: source,
                            available: amount, 
                            moveAmount: amount, 
                            onSave: async (data) => {
                                const {amount, destination, source} = data; 
                                if(destination === source)
                                    return; 
    
                                const statementId = this.state.headers.find(h => h.columnKey === month).data.statement; 
                                const statements = this.state.statements.map(s => {
    
                                    const sourceDiff = s.diff?.[source]??0; 
                                    const targetDiff = s.diff?.[destination]??0; 
                                    
                                    if(s._id !== statementId)
                                        return s; 
                                    return {
                                        ...s, 
                                        diff: {
                                            ...s.diff??{}, 
                                            [source]: Number(sourceDiff) - Number(amount),
                                            [destination]: Number(targetDiff) + Number(amount), 
                                        }
                                    }
                                });
    
                                const statement = statements.find(s => s._id === statementId);
    
                                this.setState({statements});
    
                                await api.post('/tpa/statement/update', {
                                    statementId: statementId,
                                    diff: {
                                        diff: statement.diff
                                    }
                                })
                            }
                        })
                    }
                }
            ]

            return options
        }

        let allowApproveAll = this.state.headers.every(h => (h.data?.errors?.length??0) === 0 && (h.data?.reconciles??true) && !this.state.loading); 
        // If all statements are already confirmed, don't show the approve all button.
        allowApproveAll = allowApproveAll && ((this.state.statements.some(s => !s.confirmed) && this.props.user.email.endsWith('@stax.ai')) || (this.state.statements.some(s => !s.clientConfirmed) && !this.props.user.email.endsWith('@stax.ai'))); 

        return (
            <div style={{marginTop: 50}}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: 30,
                    }}
                >
                    <h3 style={{fontWeight: 600}}>Trust Accounting</h3>
                    {
                        !this.state.statements.every(s => s.classifying) && 
                        <div
                            className='a'
                            style={{
                                marginLeft: 25, 
                                lineHeight: '33px',
                                backgroundImage: 'url("https://static.stax.ai/images/icons/action-retrain.png")',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 16, 
                                backgroundPosition: 'left 6px', 
                                paddingLeft: 20, 
                                color: this.state.disablingRefresh ? 'var(--color-text-light)' : undefined,
                                cursor: this.state.disablingRefresh ? 'not-allowed' : 'pointer'
                            }}
                            onClick={async() => {
                                if(this.state.disablingRefresh){
                                    toast.error("Updating cell value");
                                    return; 
                                }
                                this.setState({loading: true});
                                await this.fetchStatements(); 
                                await this.fetchTransactions(); 
                                this.formatToTable(); 
                                this.filterTransactions();
                                this.setState({loading: false});
                            }}  
                        >
                            Refresh data
                        </div>
                    }
                    {
                        allowApproveAll ? 
                        <div
                            className='a'
                            style={{
                                marginLeft: 25, 
                                lineHeight: '33px',
                                backgroundImage: 'url("https://static.stax.ai/images/icons/confirm.png")',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 16, 
                                backgroundPosition: 'left 6px', 
                                paddingLeft: 20, 
                            }}
                            onClick={async() => {
                                let statements;
                                if(this.props.user.email.endsWith('@stax.ai'))
                                    statements = this.state.statements.map(s => ({...s, confirmed: true}));
                                else if(!this.props.user.email.endsWith('@stax.ai'))
                                    statements = this.state.statements.map(s => ({...s,confirmed: true, clientConfirmed: true}));
                                this.setState({statements});
                                this.state.statements.forEach(async statement => {
                                    if(statement.confirmed && this.props.user.email.endsWith('@stax.ai')) return; 
                                    if(statement.clientConfirmed && !this.props.user.email.endsWith('@stax.ai')) return;
                                    await api.post('/tpa/statement/confirm', {
                                        statementId: statement._id, 
                                        source: this.props.user.email.endsWith('@stax.ai') ? 'stax': 'tpa'
                                    });
                                }); 
                            }}  
                        >
                            Approve all
                        </div>:''
                    }
                 
                    <div style={{marginLeft: 'auto'}}/>
                </div>
                <Spreadsheet 
                    loading={this.state.loading}
                    columnDefs={this.state.columnDefs}
                    rowDefs={this.state.rowDefs}
                    headers={this.state.headers}
                    cells={this.state.cells}
                    query={this.props.query}
                    renderActionOpen={true}
                    userEmail={this.props.user.email}
                />
                
                <ContextMenu
                    load={(open,close) => {
                        this.openTableMenu = open;
                        this.closeTableMenu = close;
                    }}
                    options={getContextMenuOptions}
                />
            </div>
        )

    }

    render = () => {
        return (
            <div style={{paddingLeft: 20}}>
                {this.renderWarnings()}
                {this.renderAccountInfo()}
                {this.renderTrustAccounting()}
                {this.renderTransactions()}
            </div>
        );
    }
}